import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, ShoppingBag, Check } from "lucide-react";
import { formatPrice, productUrl } from "@/lib/storefront";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

/**
 * Frequently Bought Together — interactive bundle row.
 *
 *   [main]  +  [extra1 ☐]  +  [extra2 ☐]  +  [extra3 ☐]   →  Live total
 *
 * - All extras are pre-selected so the bundle savings are visible
 * - Each tile has a checkbox (visible on the tile + an explicit row checkbox)
 * - Total recomputes live via useMemo on every toggle
 * - A modest 5% "bundle saving" appears only when 2+ items are selected
 */
const BUNDLE_DISCOUNT_PCT = 0.05;
const BUNDLE_MIN_ITEMS = 2;

export default function FrequentlyBoughtTogether({ mainProduct, items = [], maxExtras = 3 }) {
  const { addItem } = useCart();
  const extras = items.slice(0, maxExtras);
  const [selected, setSelected] = useState({});

  // (Re)initialise selection whenever the extras list changes — main + all extras pre-selected.
  useEffect(() => {
    const initial = {};
    extras.forEach((p) => { initial[p.id] = true; });
    setSelected(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((p) => p.id).join(",")]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const { selectedCount, subtotal, savings, total } = useMemo(() => {
    const base = Number(mainProduct?.price || 0);
    const selectedExtras = extras.filter((p) => selected[p.id]);
    const extrasTotal = selectedExtras.reduce((s, p) => s + Number(p.price || 0), 0);
    const sub = +(base + extrasTotal).toFixed(2);
    const itemCount = 1 + selectedExtras.length;
    const save = itemCount >= BUNDLE_MIN_ITEMS ? +(sub * BUNDLE_DISCOUNT_PCT).toFixed(2) : 0;
    return {
      selectedCount: selectedExtras.length,
      subtotal: sub,
      savings: save,
      total: +(sub - save).toFixed(2),
    };
  }, [mainProduct, extras, selected]);

  if (!mainProduct || extras.length === 0) return null;

  const addBundle = () => {
    addItem(mainProduct, 1);
    let added = 1;
    extras.forEach((p) => {
      if (selected[p.id]) {
        addItem(p, 1);
        added += 1;
      }
    });
    const msg = savings > 0
      ? `${added} items added · ${formatPrice(savings, mainProduct.currency)} bundle saving applied at checkout`
      : `${added} item${added === 1 ? "" : "s"} added to bag`;
    toast.success(msg);
  };

  return (
    <section className="mt-24 border-t border-[#E6D4CC] pt-16" data-testid="fbt-section">
      <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Pair beautifully</div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-display text-3xl text-[#2B2127] md:text-4xl">
          Frequently bought <span className="font-display-soft">together</span>
        </h2>
        <div className="text-xs text-[#6B5158]">Tick or untick to build your bundle — total updates live.</div>
      </div>

      <div className="mt-10 grid gap-8 md:grid-cols-[1fr_360px] md:items-start">
        {/* Visual row of products */}
        <div className="min-w-0">
          <div className="flex min-w-0 items-stretch gap-3 overflow-x-auto pb-2 md:gap-5">
            <BundleTile product={mainProduct} isMain isOn onToggle={() => {}} />
            {extras.map((p) => (
              <React.Fragment key={p.id}>
                <div className="flex flex-shrink-0 items-center text-[#9D5C6C]">
                  <Plus size={20} strokeWidth={1.6} />
                </div>
                <BundleTile
                  product={p}
                  isOn={!!selected[p.id]}
                  onToggle={() => toggle(p.id)}
                />
              </React.Fragment>
            ))}
          </div>

          {/* Explicit checkbox row — clearer than the tile-corner toggle */}
          <ul className="mt-6 divide-y divide-[#E6D4CC] rounded-md border border-[#E6D4CC] bg-white" data-testid="fbt-rows">
            <li className="flex items-center gap-3 px-4 py-3" data-testid={`fbt-row-${mainProduct.id}`}>
              <span className="flex h-5 w-5 items-center justify-center rounded-sm border-2 border-[#2B2127] bg-[#2B2127] text-white">
                <Check size={12} strokeWidth={3} />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#2B2127]">{mainProduct.title}</span>
              <span className="store-nums text-sm">{formatPrice(mainProduct.price, mainProduct.currency)}</span>
              <span className="hidden text-[10px] uppercase tracking-[0.2em] text-[#6B5158] md:inline">This item</span>
            </li>
            {extras.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`fbt-row-${p.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  aria-pressed={!!selected[p.id]}
                  aria-label={selected[p.id] ? `Remove ${p.title} from bundle` : `Add ${p.title} to bundle`}
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-sm border-2 transition-colors ${selected[p.id] ? "border-[#2B2127] bg-[#2B2127] text-white" : "border-[#C8C2B6] bg-white text-transparent hover:border-[#2B2127]"}`}
                  data-testid={`fbt-checkbox-${p.id}`}
                >
                  <Check size={12} strokeWidth={3} />
                </button>
                <Link to={productUrl(p)} className={`min-w-0 flex-1 truncate text-sm transition-colors hover:text-[#9D5C6C] ${selected[p.id] ? "text-[#2B2127]" : "text-[#6B5158] line-through decoration-[#C8C2B6]"}`}>{p.title}</Link>
                <span className={`store-nums text-sm transition-colors ${selected[p.id] ? "text-[#2B2127]" : "text-[#6B5158] line-through"}`}>{formatPrice(p.price, p.currency)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Summary card */}
        <aside className="rounded-md border border-[#E6D4CC] bg-white p-6 md:sticky md:top-24" data-testid="fbt-summary">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B5158]">Your bundle</div>
          <div className="mt-1 font-display text-3xl text-[#2B2127] store-nums" data-testid="fbt-total">
            {formatPrice(total, mainProduct.currency)}
          </div>
          <div className="mt-1 text-xs text-[#6B5158]">{1 + selectedCount} item{1 + selectedCount === 1 ? "" : "s"} · before tax</div>

          <dl className="mt-5 space-y-1.5 text-xs text-[#6B5158]">
            <div className="flex justify-between"><dt>Subtotal</dt><dd className="store-nums" data-testid="fbt-subtotal">{formatPrice(subtotal, mainProduct.currency)}</dd></div>
            {savings > 0 && (
              <div className="flex justify-between text-[#9D5C6C]" data-testid="fbt-savings">
                <dt>Bundle saving · 5%</dt>
                <dd className="store-nums">−{formatPrice(savings, mainProduct.currency)}</dd>
              </div>
            )}
          </dl>

          <button
            onClick={addBundle}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2B2127] px-6 py-4 text-sm text-white transition-colors hover:bg-[#9D5C6C]"
            data-testid="fbt-add-btn"
          >
            <ShoppingBag size={14} /> Add bundle to bag
          </button>
          <div className="mt-3 text-center text-[11px] text-[#6B5158]">Untick what you don't want — the total updates instantly.</div>
        </aside>
      </div>
    </section>
  );
}

function BundleTile({ product, isMain = false, isOn = false, onToggle }) {
  return (
    <div className="flex w-32 flex-shrink-0 flex-col gap-2 md:w-40" data-testid={`fbt-tile-${product.id}`}>
      <button
        type="button"
        onClick={onToggle}
        disabled={isMain}
        aria-pressed={isOn}
        className={`group relative aspect-[4/5] overflow-hidden rounded-md bg-[#F1E3DC] transition-all ${isOn ? "ring-2 ring-[#2B2127] ring-offset-2 ring-offset-[#FAF6F1]" : "ring-1 ring-[#E6D4CC] opacity-70"} ${isMain ? "cursor-default" : "hover:ring-2 hover:ring-[#9D5C6C]"}`}
        data-testid={isMain ? `fbt-main-${product.id}` : `fbt-toggle-${product.id}`}
      >
        {product.primary_image ? (
          <img
            src={product.primary_image}
            alt={product.title}
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${isOn ? "" : "grayscale"}`}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-display text-3xl text-[#6B5158]/30">·</div>
        )}
        <div className={`absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded transition-all ${isOn ? "bg-[#2B2127] text-white" : "bg-white/95 text-[#6B5158]"}`}>
          {isOn ? <Check size={12} strokeWidth={2.6} /> : <Plus size={12} strokeWidth={2.2} />}
        </div>
        {isMain && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 via-transparent to-transparent p-2 text-left">
            <span className="text-[10px] uppercase tracking-[0.2em] text-white">This item</span>
          </div>
        )}
      </button>
      <Link
        to={productUrl(product)}
        className="line-clamp-2 text-xs leading-snug text-[#2B2127] hover:text-[#9D5C6C]"
      >
        {product.title}
      </Link>
      <div className="store-nums text-xs text-[#2B2127]">{formatPrice(product.price, product.currency)}</div>
    </div>
  );
}
